import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import { FinanceBadge, formatMoney, todayInput } from "../financeUtils";

const PAGE_SIZE = 10;
const STATUSES = ["All", "Pending", "Approved", "Rejected", "Paid"];
const EMPTY_FORM = {
  expenseNumber: "",
  title: "",
  category: "General",
  amount: "",
  expenseDate: todayInput(),
  projectId: "",
  vendorId: "",
  status: "Pending",
  notes: "",
};

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadLookups = useCallback(async () => {
    try {
      const [projectsRes, vendorsRes] = await Promise.all([
        apiFetch("/api/projects"),
        apiFetch("/api/finance/vendors?status=Active"),
      ]);
      if (projectsRes.ok) setProjects(await projectsRes.json());
      if (vendorsRes.ok) setVendors(await vendorsRes.json());
    } catch {
      setProjects([]);
      setVendors([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (status !== "All") params.set("status", status);
      if (search.trim()) params.set("search", search.trim());
      const res = await apiFetch(`/api/finance/expenses?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setExpenses(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load expenses.");
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status]);

  const totalPages = Math.max(1, Math.ceil(expenses.length / PAGE_SIZE));
  const pagedExpenses = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return expenses.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [expenses, page, totalPages]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function openCreate() {
    setForm({ ...EMPTY_FORM, expenseDate: todayInput() });
    setModalOpen(true);
  }

  async function createExpense(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch("/api/finance/expenses", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          amount: Number(form.amount || 0),
          projectId: form.projectId || null,
          vendorId: form.vendorId || null,
          expenseDate: form.expenseDate ? `${form.expenseDate}T00:00:00` : null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err?.message || "Could not create expense.");
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(expenseId, nextStatus) {
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch(`/api/finance/expenses/${expenseId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (err) {
      setError(err?.message || "Could not update expense.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="finance-page">
      <PageHeader
        title="Expenses"
        subtitle={`${expenses.length} expense${expenses.length === 1 ? "" : "s"} in the current view.`}
        actions={<button className="primary" onClick={openCreate}>New Expense</button>}
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search expense title, number, category" />
        </div>
        <div className="crm-stage-filter" aria-label="Expense status">
          {STATUSES.map((item) => (
            <button key={item} type="button" className={status === item ? "active" : ""} onClick={() => setStatus(item)}>{item}</button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card finance-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>{[1, 2, 3, 4, 5].map((item) => <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />)}</div>
        ) : expenses.length === 0 ? (
          <EmptyState title="No expenses yet" subtitle="Track reimbursements, vendor bills, project costs, and admin spend." action={<button className="primary" onClick={openCreate}>New Expense</button>} />
        ) : (
          <>
            <div className="finance-table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Expense</th>
                    <th>Category</th>
                    <th>Vendor</th>
                    <th>Requested By</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedExpenses.map((expense) => (
                    <tr key={expense.id}>
                      <td>
                        <div className="finance-row-title">{expense.title}</div>
                        <div className="finance-row-subtitle">{expense.expenseNumber}</div>
                      </td>
                      <td>{expense.category}</td>
                      <td>{expense.vendorName || "-"}</td>
                      <td>{expense.requestedByUserName || "-"}</td>
                      <td>{formatDate(expense.expenseDate)}</td>
                      <td><strong>{formatMoney(expense.amount)}</strong></td>
                      <td><FinanceBadge status={expense.status} /></td>
                      <td>
                        {expense.status === "Pending" ? (
                          <div className="finance-action-row">
                            <button className="btn-success" onClick={() => updateStatus(expense.id, "Approved")} disabled={saving}>Approve</button>
                            <button className="btn-danger" onClick={() => updateStatus(expense.id, "Rejected")} disabled={saving}>Reject</button>
                          </div>
                        ) : expense.status === "Approved" ? (
                          <button className="primary" onClick={() => updateStatus(expense.id, "Paid")} disabled={saving}>Mark Paid</button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} total={expenses.length} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <form className="glass-card modal crm-modal" onSubmit={createExpense} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>New Expense</h3>
              <button type="button" className="modal-close" onClick={() => setModalOpen(false)} aria-label="Close">x</button>
            </div>
            <div className="crm-form-grid">
              <label>
                Expense Number
                <input value={form.expenseNumber} onChange={(event) => updateField("expenseNumber", event.target.value)} placeholder="Auto if blank" />
              </label>
              <label>
                Title
                <input value={form.title} onChange={(event) => updateField("title", event.target.value)} required />
              </label>
              <label>
                Category
                <input value={form.category} onChange={(event) => updateField("category", event.target.value)} />
              </label>
              <label>
                Amount
                <input type="number" min="0" value={form.amount} onChange={(event) => updateField("amount", event.target.value)} required />
              </label>
              <label>
                Expense Date
                <input type="date" value={form.expenseDate} onChange={(event) => updateField("expenseDate", event.target.value)} />
              </label>
              <label>
                Vendor
                <select value={form.vendorId} onChange={(event) => updateField("vendorId", event.target.value)}>
                  <option value="">No vendor</option>
                  {vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}
                </select>
              </label>
              <label>
                Project
                <select value={form.projectId} onChange={(event) => updateField("projectId", event.target.value)}>
                  <option value="">No project</option>
                  {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                </select>
              </label>
              <label>
                Status
                <select value={form.status} onChange={(event) => updateField("status", event.target.value)}>
                  {STATUSES.filter((item) => item !== "All").map((item) => <option key={item}>{item}</option>)}
                </select>
              </label>
              <label className="crm-form-wide">
                Notes
                <textarea value={form.notes} onChange={(event) => updateField("notes", event.target.value)} rows={3} />
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Save Expense"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
